import { TFunction } from "i18next";
import { SchedulerStoreType } from "../state/scheduler.store";
import { logErrorAndNotifyUserForRoosterInitiatedLinks } from "../services/api/errorLoggerAndNotifier";
import { NoAvailabilityReason } from "@rooster/types";

export const setErrorStateGeneric = (
  code: string,
  message: string,
  description: string,
  actions: SchedulerStoreType["actions"],
  ui?: SchedulerStoreType["ui"]
) => {
  actions.setUiState({
    ...ui,
    currentView: "month",
    currentStep: "initial",
    error: {
      code,
      message,
      description,
      type: "persistent"
    }
  });
};

export const setLinkNotFoundState = (
  actions: SchedulerStoreType["actions"],
  message: string,
  description: string
) => {
  actions.setUiState({
    currentView: "month",
    currentStep: "initial",
    error: {
      code: "404",
      message,
      description,
      type: "persistent"
    }
  });
};

export const setUnableToLoadAvailabilityForParticipantError = (
  actions: SchedulerStoreType["actions"],
  t: TFunction
) => {
  actions.setUiState({
    currentView: "month",
    currentStep: "initial",
    error: {
      code: "UNABLE_TO_FECTH_PARTICIPANTS_AVAILABILITY",
      message: t(
        "ROOSTER.PUBLIC-APP.UNABLE_TO_FETCH_PARTICIPANTS_AVAILABILITY",
        {
          defaultValue: "Unable to fetch participants availability"
        }
      ),
      description: t(
        "ROOSTER.PUBLIC-APP.UNABLE_TO_FETCH_PARTICIPANTS_AVAILABILITY_DESC",
        {
          defaultValue:
            "We are unable to fetch availability for some participants. Please try again later. If problem persists, try contacting your recruiter or scheduler."
        }
      ),
      type: "persistent"
    }
  });
};

export const setLinkHasExpiredError = (
  actions: SchedulerStoreType["actions"],
  mode: "autobook" | "availability",
  t: TFunction,
  showGenericError = false
) => {
  if (showGenericError) {
    actions.setUiState({
      currentView: "month",
      currentStep: "initial",
      error: {
        code: "LINK_EXPIRED",
        message: t("ROOSTER.PUBLIC-APP.LINK_EXPIRED", {
          defaultValue: "Link has expired"
        }),
        description: t("ROOSTER.PUBLIC-APP.LINK_EXPIRED_DESC", {
          defaultValue:
            "This link has expired. Please contact your recruiter or scheduler to get a new link."
        }),
        type: "persistent"
      }
    });
    return;
  }

  if (mode === "autobook") {
    actions.setUiState({
      currentView: "month",
      currentStep: "initial",
      error: {
        code: "LINK_EXPIRED",
        message: t("ROOSTER.PUBLIC-APP.NO-AVAILABILITY-TITLE", {
          defaultValue: "No available dates"
        }),
        description: t("ROOSTER.PUBLIC-APP.NO-AVAILABILITY-DESC", {
          defaultValue:
            "There are no more available dates for your interview. Please contact your recruiter to schedule an interview."
        }),
        type: "persistent"
      }
    });
    return;
  }

  if (mode === "availability") {
    actions.setUiState({
      currentView: "month",
      currentStep: "initial",
      error: {
        code: "LINK_EXPIRED",
        message: t("ROOSTER.PUBLIC-APP.NO-AVAILABILITY-TITLE", {
          defaultValue: "No available dates"
        }),
        description: t("ROOSTER.PUBLIC-APP.NO-AVAILABILITY-DESC", {
          defaultValue:
            "There are no more available dates from which to choose times. Please contact your recruiter to provide times."
        }),
        type: "persistent"
      }
    });
    return;
  }
};

export const setNoAvailabilityError = (
  actions: SchedulerStoreType["actions"],
  t: TFunction
) => {
  actions.setUiState({
    currentView: "month",
    currentStep: "initial",
    error: {
      code: "NO_AVAILABILITY",
      message: t("ROOSTER.PUBLIC-APP.NO_AVAILABILITY", {
        defaultValue: "No availability found"
      }),
      description: t("ROOSTER.PUBLIC-APP.NO_AVAILABILITY_DESC", {
        defaultValue:
          "There are no available slots. Please try again later or contact your recruiter or scheduler."
      }),
      type: "persistent"
    }
  });
};

export const handleRoosterLinkValidationErrorMessageState = (
  hasError: boolean,
  isCancelled: boolean,
  error: {
    code?: string;
    invitedBy?: string;
  },
  actions: SchedulerStoreType["actions"],
  ui: SchedulerStoreType["ui"],
  host: SchedulerStoreType["host"],
  isProspectLink: boolean,
  t: TFunction
) => {
  if (hasError) {
    // when the link is not validated via API and invitedBy is present
    if (error?.code && host?.name) {
      setErrorStateGeneric(
        error.code,
        isProspectLink
          ? `Prospect Link ${t("ROOSTER.PUBLIC-APP.ROOSTER_LINK_ERROR-v2", {
              defaultValue: "Error"
            })}`
          : `RoosterLink ${t("ROOSTER.PUBLIC-APP.ROOSTER_LINK_ERROR-V2", {
              defaultValue: "Error"
            })}`,
        `${t("ROOSTER.LINKS.BOOKING_ERROR_MESSAGE", {
          hostName: host.name,
          defaultValue:
            "There is an issue with processing your request, and we are unable to schedule the meeting. A notification has been sent to {{hostName}}. For further questions, please reach out to: {{hostName}} at"
        })} ${host.email}`,
        actions,
        ui
      );
      return;
    }

    if (error?.code && !host?.name) {
      setErrorStateGeneric(
        error.code,
        t("ROOSTER.PUBLIC-APP.ROOSTER_LINK_ERROR-V2", {
          defaultValue: "Error"
        }),
        t("ROOSTER.LINKS.BOOKING_ERROR_MESSAGE_WITHOUT_HOST", {
          defaultValue:
            "There is an issue with processing your request, and we are unable to schedule the meeting. For further questions, please reach out to the meeting host."
        }),
        actions,
        ui
      );
      return;
    }

    setErrorStateGeneric(
      "404",
      t("ROOSTER.PUBLIC-APP.ROOSTER_LINK_ERROR", {
        defaultValue: "Error"
      }),
      t("ROOSTER.PUBLIC-APP.INVALID_ROOSTER_LINK-V2", {
        defaultValue: "The link you are trying to access is invalid."
      }),
      actions,
      ui
    );
  }

  if (isCancelled && ui?.currentStep !== "scheduling_cancelled") {
    setErrorStateGeneric(
      "MEETING_CANCELLED",
      t("ROOSTER.PUBLIC-APP.MEETING_CANCELLED", {
        defaultValue: "Meeting Cancelled"
      }),
      t("ROOSTER.PUBLIC-APP.MEETING_CANCELLED_DESC", {
        defaultValue: "The meeting you are trying to access has been cancelled."
      }),
      actions,
      ui
    );
  }
};

export const handleRoosterAutobookAvailabilityValidationErrorMessageState = (
  actions: SchedulerStoreType["actions"],
  {
    code,
    initiatorName,
    initiatorEmail
  }: { code: string; initiatorName?: string; initiatorEmail?: string },
  t: TFunction
) => {
  setErrorStateGeneric(
    code,
    t("ROOSTER.PUBLIC-APP.ROOSTER_LINK_ERROR-V2", {
      defaultValue: "Error"
    }),
    t("ROOSTER.PUBLIC-APP.AUTOBOOK_AVAILABILITY_LINK_ERROR_MESSAGE", {
      initiatorName,
      initiatorEmail,
      defaultValue:
        "There is an issue with processing your request, and we are unable to schedule the meeting. A notification has been sent to {{initiatorName}}. For further questions, please reach out to: {{initiatorName}} at {{initiatorEmail}}"
    }),
    actions
  );
  return;
};

export const handleRoosterAutobookAvailabilityGenericErrorMessageState = (
  actions: SchedulerStoreType["actions"],
  {
    code,
    initiatorName,
    initiatorEmail
  }: { code: string; initiatorName?: string; initiatorEmail?: string },
  t: TFunction
) => {
  setErrorStateGeneric(
    code,
    t("ROOSTER.PUBLIC-APP.ROOSTER_LINK_ERROR-V2", {
      defaultValue: "Error"
    }),
    t("ROOSTER.PUBLIC-APP.AUTOBOOK_AVAILABILITY_LINK__GENERIC_ERROR_MESSAGE", {
      initiatorName,
      initiatorEmail,
      defaultValue:
        "There is an issue with processing your request, and we are unable to schedule the meeting. For further questions, please reach out to: {{initiatorName}} at {{initiatorEmail}}"
    }),
    actions
  );
  return;
};

export const setUserGrantAsInvalidError = (
  actions: SchedulerStoreType["actions"],
  t: TFunction
) => {
  actions.setUiState({
    currentView: "month",
    currentStep: "initial",
    error: {
      code: "NO_AVAILABILITY",
      message: t("ROOSTER.PUBLIC-APP.ERROR-OCCURED", {
        defaultValue: "An Error Occured"
      }),
      description: t(
        "ROOSTER.PUBLIC-APP.UNABLE_TO_CONNECT_RECRUITER_CALENDAR",
        {
          defaultValue:
            "Unable to connect with recruiter's calendar. Please click the button below to retry. If issue persists contact your recruiter."
        }
      ),
      primaryButtonText: t("ROOSTER.PUBLIC-APP.RETRY", {
        defaultValue: "Retry"
      }),
      primaryButtonAction: () => {
        window.location.reload();
      },
      type: "dismissible"
    }
  });
};

// R495-619 (QA follow-up): the store starts at `currentStep: "loading"`, so ANY
// initialisation path that returns without setting a step leaves the candidate
// on a permanent "Loading meeting information" skeleton — no error, no retry,
// no way to tell a dead link from a slow one. A null `linkType` does exactly
// that today, and a throw anywhere in the init chain has the same effect.
//
// This is the terminal fallback for that class: it says the page could not be
// loaded and offers the same reload the grant-invalid error uses. It is
// deliberately generic — at this point we know only that initialisation did
// not complete, not why — and it must never overwrite a specific error that a
// handler already set (the caller checks the step before invoking it).
export const setInitializationFailedState = (
  actions: SchedulerStoreType["actions"],
  t: TFunction
) => {
  actions.setUiState({
    currentView: "month",
    currentStep: "initial",
    error: {
      code: "NO_AVAILABILITY",
      message: t("ROOSTER.PUBLIC-APP.ERROR-OCCURED", {
        defaultValue: "An Error Occured"
      }),
      description: t("ROOSTER.PUBLIC-APP.UNABLE_TO_LOAD_MEETING", {
        defaultValue:
          "We could not load this interview request. Please click the button below to retry. If the issue persists contact your recruiter."
      }),
      primaryButtonText: t("ROOSTER.PUBLIC-APP.RETRY", {
        defaultValue: "Retry"
      }),
      primaryButtonAction: () => {
        window.location.reload();
      },
      type: "dismissible"
    }
  });
};

export const notifySenderOnNoAvailability = async (
  noAvailabilityReason: NoAvailabilityReason,
  link: string,
  sessionData: { isRescheduleFlow?: boolean }
) => {
  if (
    noAvailabilityReason?.reason &&
    [
      "BOTH_GROUPS_INSUFFICIENT_CAPACITY",
      "ROUND_ROBIN_INSUFFICIENT_CAPACITY",
      "COLLECTIVE_UTILIZATION_CAPS_EXHAUSTED",
      "UTILIZATION_CAPS_EXHAUSTED"
    ].includes(noAvailabilityReason.reason)
  ) {
    await logErrorAndNotifyUserForRoosterInitiatedLinks({
      errorCode: "ROOSTER_NO_AVAILABILITY_DUE_TO_CAPACITY",
      context: { linkId: link ?? "" },
      noAvailabilityReason,
      isReschedule: sessionData.isRescheduleFlow ?? false
    });
  } else if (noAvailabilityReason?.reason === "NO_ROOSTER_BLOCK_AVAILABILITY") {
    await logErrorAndNotifyUserForRoosterInitiatedLinks({
      errorCode: "NO_ROOSTER_BLOCK_AVAILABILITY",
      context: { linkId: link ?? "" },
      noAvailabilityReason,
      isReschedule: sessionData.isRescheduleFlow ?? false
    });
  } else {
    await logErrorAndNotifyUserForRoosterInitiatedLinks({
      errorCode: "ROOSTER_NO_AVAILABILITY_GENERIC_AUTOBOOK",
      context: { linkId: link ?? "" },
      noAvailabilityReason,
      isReschedule: sessionData.isRescheduleFlow ?? false
    });
  }
};
